<script lang="ts" setup>
	import { useScroll } from '@vueuse/core'
	import type { NavLinksContext } from './nav-links.vue'
	import StringMethods from '~/utils/character'

	const currentPlatform = shallowRef(0)
	const { productIcon } = useUtils()
	const { isMobile } = useMobile()
	const { showProductName, toLower } = StringMethods()

	const activeTab = shallowRef('')
	const selectedLink = shallowRef('')

	const isMenuOpen = shallowRef(false)

	const isDialogOpen = shallowRef(false)
	const isClaimsModal = shallowRef(false)
	const platformList = shallowRef([
		{
			key: 'business',
			label: 'Business',
			icon: 'briefcase',
			src: '/',
			description: false
		},
		{
			key: 'individual',
			label: 'Personal',
			icon: 'persons',
			src: '/individual',
			description: false
		}
	])
	const { name } = useRoute()
	const productLinks = computed(() => {
		const { productLinks } = useProductsStore()
		return productLinks.map(item => String(item.url).replace('/', ''))
	})
	const activePlatform = computed(() => platformList.value[currentPlatform.value].key ?? '')
	const isUserAccount = computed(() => activePlatform.value === 'individual')
	const routeName = computed(() => useRoute().name)
	const selectedPlatform = shallowRef('business')
	const platformDropdown = shallowRef(false)
	type NavLinksType = {
		[id: string]: {
			role: string
			active?: boolean
			url?: string
			target?: '_blank' | '_self' | ''
			linkList?: NavLinksContext
			action?: () => void
		}
	}

	const routeSetup = (arg: string, category?: string) => {
		if (isUserAccount.value) {
			const replace = useRoute().name === 'individual'
			return { path: '/individual#pricing', query: { product: String(arg), category, hash: 'pricing' }, replace }
		}
		return `/${arg}`
	}
	const navLinks = computed<NavLinksType>(() => ({
		products: {
			role: 'button',
			active: false,
			linkList: [
				{
					category: 'Health',
					open: false,
					list: [
						{
							title: 'FlexiCare',
							desc: !isUserAccount.value
								? 'Unlock access to quality healthcare for your staff.'
								: 'Access quality healthcare for yourself and your family.',
							link: routeSetup(`flexicare`, 'health'),
							icon: {
								dir: 'fill',
								name: 'heart-beat',
								wrapperClass: 'border-blue-500'
							}
						},
						{
							title: 'ZenCare',
							desc: !isUserAccount.value
								? 'The all-in-one health insurance for bigger teams.'
								: 'Comprehensive health insurance for you and your loved ones.',
							link: routeSetup(`zencare`, 'health'),
							icon: {
								dir: 'fill',
								name: 'heart-beat',
								wrapperClass: 'border-pink-500',
								pathClass: 'fill-pink-500'
							}
						},
						{
							title: 'PrimeCare',
							desc: !isUserAccount.value
								? 'Boost team productivity with premium healthcare'
								: 'Premium healthcare to keep you healthy and productive.',
							link: routeSetup(`primecare`, 'health'),
							icon: {
								dir: 'fill',
								name: 'heart-beat',
								wrapperClass: 'border-success-500',
								pathClass: 'fill-success-500'
							}
						}
					]
				},
				{
					category: 'Auto',
					open: false,
					list: [
						{
							title: 'Comprehensive Cover',
							desc: !isUserAccount.value
								? 'Cover all the vehicles used to drive growth in your business.'
								: 'Full protection for your vehicle.',
							link: routeSetup(`comprehensive-cover`, 'auto'),
							icon: {
								dir: 'random',
								name: 'auto-car',
								wrapperClass: 'border-blue-500'
							}
						},
						{
							title: 'Mini Comprehensive',
							desc: !isUserAccount.value
								? 'Get your vehicles on the road without worry on your budget.'
								: 'Affordable coverage for your vehicle.',
							link: routeSetup(`mini-comprehensive`, 'auto'),
							icon: {
								dir: 'random',
								name: 'auto-car',
								wrapperClass: 'border-purple-500',
								pathClass: 'fill-purple-500'
							}
						},
						{
							title: 'Third Party',
							desc: !isUserAccount.value
								? 'Drive with confidence while you save on insurance costs.'
								: 'Essential protection for third party vehicles.',
							link: routeSetup(`third-party`, 'auto'),
							icon: {
								dir: 'random',
								name: 'auto-car',
								wrapperClass: 'border-orange-500',
								pathClass: 'fill-orange-500'
							}
						}
					]
				},
				{
					category: 'Others',
					open: false,
					list: [
						{
							title: 'Gadget Cover',
							desc: !isUserAccount.value
								? 'Secure company devices such as mobile phones and laptops.'
								: 'Secure your devices: laptops and phones from unexpected damage.',
							link: routeSetup(`gadget`, 'gadget'),
							icon: {
								dir: 'random',
								name: 'monitor-mobile',
								wrapperClass: 'border-purple-500',
								pathClass: 'fill-purple-500'
							}
						},
						{
							title: 'Travel Cover',
							desc: !isUserAccount.value
								? 'Experience the world happy with the right cover.'
								: 'Travel with peace of mind, covered anywhere.',
							link: routeSetup(`travel-cover`, 'travel'),
							icon: {
								dir: 'random',
								name: 'airplane-2',
								wrapperClass: 'border-pink-500'
							}
						}
					]
				}
			]
		},
		claims: {
			role: 'button',
			action: () => {
				isClaimsModal.value = true
			}
		},
		resources: {
			role: 'button',
			active: true,
			linkList: [
				{
					category: '',
					list: [
						{
							title: 'About Us',
							desc: 'Discover our people, our story, our mission, and our values.',
							icon: {
								name: 'mcg-cap-sm',
								dir: 'internal-logo',
								wrapperClass: 'border-main',
								width: 20,
								height: 20,
								pathClass: 'fill-base-primary'
							},
							link: '/about-us'
						}
					]
				},
				{
					category: '',
					list: [
						{
							title: 'Product Guide',
							desc: 'Get detailed information and learn more about all our products.',
							icon: {
								name: 'map',
								dir: 'random',
								wrapperClass: 'border-auto-500'
							},
							link: '/product-guide'
						}
					]
				},
				{
					category: '',
					list: [
						{
							title: 'Blog',
							desc: 'Get news, tips, updates and best practices for insurance.',
							icon: {
								name: 'document-text',
								dir: 'random',
								wrapperClass: 'border-gadget-500'
							},
							link: '/blog'
						}
					]
				}
			]
		},
		API: {
			url: 'https://mycover.ai/',
			role: 'link',
			target: '_blank'
		}
	}))
	const { y } = useScroll(import.meta.client ? document : null)
	const scrollHeight = ref(0)

	onMounted(() => {
		scrollHeight.value = y.value
		watch(y, val => {
			scrollHeight.value = val
		})
	})

	const isOnBase = computed(() => {
		const { path, name } = useRoute()
		const pathName = path.split('/')[1].toLowerCase()
		const list = ['index', 'individual', 'about-us', 'blog', 'guide']

		return list.includes(pathName) || list.includes(String(name))
	})
	const currentPageName = computed(() => String(useRoute().name))

	const replaceIndividualLabel = (arg: string) => String(arg).replace('individual', 'Personal')
	const openTab = (arg: string) => {
		if (!activeTab.value || activeTab.value !== arg) {
			activeTab.value = arg
			isMenuOpen.value = true
			return null
		}
		activeTab.value = ''
		isMenuOpen.value = false
	}
	const clear = () => {
		isMenuOpen.value = false
		isDialogOpen.value = false
		activeTab.value = ''
	}

	watch(
		() => routeName.value,
		val => {
			currentPlatform.value = platformList.value.some(item => item.key === val) ? 1 : 0
		}
	)
</script>

<template>
	<header class="min-h-[100px]">
		<nav class="w-full max-h-[100px] py-6 bg-current dark:bg-gray-800 soft-shadow fixed top-0 left-0 z-[500]">
			<lazy-ui-pad
				class="flex items-center w-full h-full justify-between mx-auto px-5"
				:ui="{ paddingBlock: '' }"
			>
				<div class="flex items-center flex-none sm:gap-x-4 gap-x-2" data-description="mycovergenius logo">
					<div
						class="h-[40px] w-[40px] bg-base-50 md:bg-transparent md:h-auto md:w-auto md:border-none border border-base-100 flexed justify-center rounded-full"
					>
						<nuxt-link to="/" title="home" class="relative">
							<span
								class="custom-transition-2 hidden md:inline"
								:class="{
									'opacity-100':
										((!scrollHeight || scrollHeight < 100) && isOnBase) || !currentPageName,
									'opacity-0 absolute pointer-events-none':
										(scrollHeight > 100 || !isOnBase) && currentPageName
								}"
							>
								<tools-icon dir="internal-logo" icon="mcg-logo-black" :height="40" :width="145" />
							</span>
							<span
								:class="{
									'md:opacity-0 md:absolute  pointer-events-none':
										(scrollHeight < 100 && isOnBase) || !currentPageName,
									'opacity-100': scrollHeight > 100 && isOnBase && currentPageName
								}"
							>
								<tools-icon
									dir="internal-logo"
									icon="mcg-cap-sm"
									:width="49"
									:height="40"
									svg-class="lg:h-auto lg:w-auto w-[33.05px] h-[19.43px]"
									path-class="fill-base-primary"
								/>
							</span>
						</nuxt-link>
					</div>

					<molecules-platform-tab
						v-if="isOnBase || !productLinks.includes(toLower(name))"
						class="hidden lg:inline-flex"
					/>
					<div
						v-if="isOnBase || productLinks.includes(toLower(name))"
						class="min-h-[36px] capitalize lg:border flex-none font-semibold text-main text-xs items-center gap-x-1 rounded-[20px] border-main bg-base-50 px-4 cursor-pointer"
						:class="{
							'flex lg:hidden': isOnBase,
							'inline-flex': !isOnBase
						}"
						@click="() => (platformDropdown = isMobile && !platformDropdown)"
					>
						{{ isUserAccount ? platformList[1].label : replaceIndividualLabel(selectedPlatform) }}
						<span class="lg:inline-flex hidden capitalize items-center gap-x-1">
							/
							<tools-icon v-bind="productIcon(currentPageName)" />
							{{ showProductName(currentPageName) }}
						</span>
						<span class="lg:hidden">
							<tools-icon dir="arrows" icon="caret-down" path-class="stroke-blue-950" :width="15" />
						</span>
					</div>
				</div>

				<div class="inline-flex w-full justify-end items-center" data-description="nav links">
					<ul class="md:flex items-center xl:gap-x-5 xl:mr-12 hidden">
						<li v-for="(item, k, index) in navLinks" :key="index" @click="item.action">
							<a
								:href="item?.url"
								:role="item.role || 'link'"
								aria-label="nav link"
								class="py-3 inline-flex gap-x-2 border-transparent text-gray-700 dark:text-gray-400 capitalize border px-5 sm:rounded-[300px] transition-all ease-linear font-medium text-sm"
								:class="{
									'bg-main !text-white': activeTab === k,
									'hover:border-base-primary hover:!text-base-primary group': activeTab !== k
								}"
								:target="item.target || '_self'"
								@click=";[item.linkList ? openTab(k) : null]"
							>
								<span>{{ k }}</span>
								<span
									v-show="Array.isArray(item.linkList) && item.linkList.length"
									:class="[activeTab === k ? '-rotate-180' : 'rotate-0', 'custom-transition-2']"
								>
									<tools-icon
										dir="arrows"
										icon="caret-down"
										:path-class="`stroke-gray-500 group-hover:stroke-base-primary ${
											(activeTab === k && 'stroke-white') || ''
										}`"
									/>
								</span>
							</a>
						</li>
					</ul>

					<ui-btn
						class="capitalize hover:bg-base-primary flex-none bg-main text-white"
						name="login"
						:ui="{
							btn: {
								padding: 'p-[12px_30px]',
								width: 'max-w-[130px]',
								onHoverPseudo: 'hover:before:bg-base-primary'
							}
						}"
						@click="() => (isDialogOpen = true)"
					>
						<template #icon>
							<tools-icon dir="fill" icon="person" width="19" height="18" />
						</template>
						<span class="capitallize">login</span>
					</ui-btn>
					<ui-hamburgger v-model:show="isMenuOpen" class="md:hidden" />
				</div>
			</lazy-ui-pad>

			<div
				class="menu bg-current pb-10 dark:bg-gray-900 sm:px-5 px-3 custom-transition-2 soft-shadow md:absolute fixed left-0 md:w-full overflow-hidden overflow-y-auto"
				:class="{
					'invisible h-0 w-0': !isMenuOpen,
					'visible  sm:h-[600px] h-full w-full': isMenuOpen
				}"
			>
				<lazy-ui-pad class="mx-auto mt-10">
					<div
						v-if="activeTab === 'products'"
						class="md:flex hidden items-center justify-center sticky top-0"
					>
						<u-tabs
							:items="platformList"
							v-model="currentPlatform"
							:ui="{
								list: {
									background: 'bg-base-50 min-w-[280px]',
									rounded: '',
									padding: '',
									marker: {
										wrapper:
											'absolute !top-[7px] left-[4px] duration-200 ease-out focus:outline-none',
										background: '!bg-transparent border-b-2 border-base-primary',
										rounded: 'rounded-none',
										shadow: ''
									},
									tab: {
										active: 'text-main  bg-transparent',
										rounded: 'rounded-none last:border-l border-gray-300 dark:border-gray-500',
										size: 'text-xs'
									}
								}
							}"
						>
							<template #icon="{ item, selected }">
								<span class="mr-2">
									<tools-icon :dir="selected ? 'fill' : 'line'" :icon="item.icon" />
								</span>
							</template>
						</u-tabs>
					</div>

					<h4
						class="text-gray-700 dark:text-gray-200 font-semibold text-xl w-full my-5 md:block hidden"
						:class="{ 'mt-20': activeTab !== 'products' }"
					>
						{{ activeTab === 'products' ? `Discover our ${activePlatform} products` : 'Resources' }}
					</h4>
					<div class="flex lg:flex-nowrap flex-wrap items-start w-full justify-between">
						<div class="flex-1">
							<template v-for="(item, k, index) in navLinks" :key="index">
								<div
									class="flex items-center md:hidden justify-between py-4 mt-6 md:mt-0 md:pb-5 md:border-b-0 border-b dark:border-gray-600 w-full"
									role="button"
									aria-label="nav link`"
									:title="k"
									@click="
										;[
											(selectedLink = !selectedLink || selectedLink !== k ? k : ''),
											navLinks[k]?.action ? navLinks[k].action() : null
										]
									"
								>
									<h5 class="font-medium text-blue-950 dark:text-gray-300 capitalize text-lg">
										{{ k }}
									</h5>
									<span
										:class="{
											'rotate-0': selectedLink !== k,
											'rotate-180': selectedLink === k
										}"
										class="custom-transition-2"
									>
										<tools-icon
											v-if="item.linkList"
											dir="arrows"
											icon="caret-down"
											path-class="stroke-gray-600"
										/>
									</span>
								</div>

								<div
									class="custom-transition-2 overflow-hidden overflow-y-auto md:overflow-visible"
									:class="{
										'md:hidden block': k !== activeTab,
										'max-h-0': selectedLink !== k,
										'max-h-[700px]': selectedLink === k
									}"
								>
									<header-nav-links v-if="item.linkList" :links="item.linkList" @change="clear" />
								</div>
							</template>
						</div>

						<div
							v-if="activeTab !== 'resources'"
							class="sm:max-w-[266px] md:block hidden flex-1 space-y-5 w-full"
						>
							<div class="min-h">
								<nuxt-img
									provider="cloudinary"
									:src="
										!isUserAccount
											? '/v1727082954/sales_tth1q7.webp'
											: '/v1727519073/excited_d1qtkt.webp'
									"
									alt="sales rep"
									loading="lazy"
								/>
							</div>
							<p class="font-medium text-xs sm:leading-[20px] text-gray-600">
								Here to guide your Business through the confusing insurance patterns and processes.
							</p>
							<button
								type="button"
								name="customer support"
								class="text-sm custom-transition-3 hover:text-base-primary text-gray-600 font-medium cursor-pointer group inline-flex items-center gap-x-3"
								id="intercom_custom_btn"
							>
								Contact Sales
								<span class="group-hover:translate-x-2 custom-transition-3">
									<tools-icon icon="arrow-short-r" dir="arrows" path-class="fill-gray-600" />
								</span>
							</button>
						</div>

						<div
							v-else
							class="max-h-[1450px] lg:inline hidden max-w-[226px] w-full rounded border border-travel-100 bg-travel-50 relative overflow-hidden"
						>
							<span class="absolute -bottom-3 w-full h-full left-0">
								<tools-icon icon="background-wave" dir="random" />
							</span>
							<div class="relative h-[inherit] mx-auto p-5 w-full z-10 space-y-3">
								<tools-icon dir="internal-logo" icon="mcg-logo-black" :width="90" :height="22.34" />
								<p class="text-gray-700 text-xs sm:leading-[18px]">
									Africa's leading insure-tech platform offering affordable insurance plans for
									businesses and individuals
								</p>
							</div>
						</div>
					</div>
				</lazy-ui-pad>
			</div>
		</nav>

		<lazy-molecules-auth-dialog v-model="isDialogOpen" selected="login" />

		<lazy-molecules-claims-dialog v-model="isClaimsModal" />

		<lazy-ui-modal v-model:show="platformDropdown">
			<div class="mx-auto w-full text-[#094163]">
				<ui-title-header
					title="Select your preference"
					class="py-8 bg-base-50 mx-auto custom-transition-2 dark:border-gray-700"
					:ui="{ size: 'sm:text-[3em] text-[1.5em] text-center' }"
				/>

				<div class="gap-10 space-y-8 px-5 pb-5 my-8">
					<nuxt-link
						v-for="(link, index) in platformList"
						:key="index"
						:to="link.src"
						class="relative w-full group custom-transition-2 dark:border-gray-700 hover:bg-base-50 hover:dark:bg-gray-800 hover:border-main border border-gray-200 rounded-[4px] p-[16px_12px] flex gap-x-5 items-center"
						@click="() => (platformDropdown = false)"
					>
						<div
							class="h-[32px] flex-none w-[32px] custom-transition-2 flex items-center justify-center group-hover:bg-white rounded-full bg-base-50"
						>
							<tools-icon :icon="link.icon" dir="fill" :height="18" :width="18" />
						</div>

						<div class="w-full flex justify-between items-center">
							<h4 class="font-semibold sm:text-lg text-base text-blue-950 dark:text-main">
								For {{ link.label }}
							</h4>
						</div>

						<div class="relative custom-transition-2 animate-x opacity-0 group-hover:opacity-100">
							<tools-icon
								icon="arrow-short-r"
								dir="arrows"
								path-class="fill-gray-700 dark:fill-gray-400"
							/>
						</div>
					</nuxt-link>
				</div>
			</div>
		</lazy-ui-modal>
	</header>
</template>
